/**
 * Shared ticket availability logic
 *
 * This composable centralizes all ticket availability calculations to ensure
 * consistency across camp-details, listings, booking dialog, and checkout.
 */

export interface AvailabilityDay {
  date: string
  available: number
  capacity?: number
  weekTicketsSold?: number
  dayTicketsSold?: number
}

export interface AvailabilityWeekTicket {
  ticket_type_id: string
  name: string
  available: number
  capacity?: number
  weekTicketsSold?: number
}

export interface AvailabilityData {
  campId: string
  maxAttendees: number
  weekTicketsSold: number
  weekTicketAvailability: AvailabilityWeekTicket[]
  dayAvailability: AvailabilityDay[]
}

export interface AvailabilitySummary {

  /** At least one week ticket type has availability */
  weekAvailable: boolean

  /** At least one day has availability for day tickets */
  dayAvailable: boolean

  /** Day tickets available but NO week tickets available */
  limited: boolean

  /** No tickets available at all (neither week nor day) */
  soldOut: boolean

  /** Total week tickets sold */
  weekTicketsSold: number

  /** Minimum day tickets sold across all days (used for progress bar) */
  minDayTicketsSold: number

  /** Maximum day tickets sold across all days */
  maxDayTicketsSold: number

  /** Effective capacity used: weekTicketsSold + minDayTicketsSold */
  effectiveUsed: number

  /** Maximum attendees for the camp */
  maxAttendees: number

  /** Effective remaining spots */
  effectiveRemaining: number
}

/**
 * Build a comprehensive availability summary from API data
 */
export function buildAvailabilitySummary(data: AvailabilityData): AvailabilitySummary {
  const weekAvailable = data.weekTicketAvailability.some(ticket => ticket.available > 0)
  const dayAvailable = data.dayAvailability.some(day => day.available > 0)

  let minDayTicketsSold = 0
  let maxDayTicketsSold = 0

  if (data.dayAvailability.length > 0) {
    const daySales = data.dayAvailability.map(d => d.dayTicketsSold ?? 0)

    minDayTicketsSold = Math.min(...daySales)
    maxDayTicketsSold = Math.max(...daySales)
  }

  // Effective used capacity: week tickets take all days, plus minimum day tickets
  // This gives the "best case" remaining capacity
  const effectiveUsed = data.weekTicketsSold + minDayTicketsSold
  const effectiveRemaining = Math.max(0, data.maxAttendees - effectiveUsed)

  return {
    weekAvailable,
    dayAvailable,
    limited: dayAvailable && !weekAvailable,
    soldOut: !dayAvailable && !weekAvailable,
    weekTicketsSold: data.weekTicketsSold,
    minDayTicketsSold,
    maxDayTicketsSold,
    effectiveUsed,
    maxAttendees: data.maxAttendees,
    effectiveRemaining,
  }
}

/**
 * Check if a specific ticket type is sold out based on availability data
 */
export function isTicketTypeSoldOut(
  ticketTypeId: string,
  ticketType: 'week' | 'day',
  availabilityData: AvailabilityData | null,
): boolean {
  if (!availabilityData)
    return false

  if (ticketType === 'week') {
    const weekAvail = availabilityData.weekTicketAvailability.find(
      w => w.ticket_type_id === ticketTypeId,
    )

    return weekAvail ? weekAvail.available <= 0 : false
  }

  // For day tickets, check if ALL days are sold out
  if (ticketType === 'day') {
    if (availabilityData.dayAvailability.length === 0)
      return false

    return availabilityData.dayAvailability.every(day => day.available <= 0)
  }

  return false
}

/**
 * Get availability for a specific day
 */
export function getDayAvailability(
  date: string,
  availabilityData: AvailabilityData | null,
): { available: number, soldOut: boolean } {
  if (!availabilityData)
    return { available: Infinity, soldOut: false }

  const dayInfo = availabilityData.dayAvailability.find(d => d.date === date)
  if (!dayInfo)
    return { available: Infinity, soldOut: false }

  return {
    available: dayInfo.available,
    soldOut: dayInfo.available <= 0,
  }
}

/**
 * Get availability for a specific week ticket type
 */
export function getWeekTicketAvailability(
  ticketTypeId: string,
  availabilityData: AvailabilityData | null,
): { available: number, soldOut: boolean } {
  if (!availabilityData)
    return { available: Infinity, soldOut: false }

  const weekInfo = availabilityData.weekTicketAvailability.find(
    w => w.ticket_type_id === ticketTypeId,
  )

  if (!weekInfo)
    return { available: Infinity, soldOut: false }

  return {
    available: weekInfo.available,
    soldOut: weekInfo.available <= 0,
  }
}

/**
 * Calculate progress bar percentage for camp availability display
 * Returns percentage of REMAINING capacity (for display as "spots left")
 */
export function getCapacityPercentage(summary: AvailabilitySummary): number {
  if (summary.maxAttendees <= 0)
    return 0
  const remainingPercentage = (summary.effectiveRemaining / summary.maxAttendees) * 100

  return Math.min(100, Math.max(0, remainingPercentage))
}

/**
 * Get the appropriate color for capacity display
 */
export function getCapacityColor(summary: AvailabilitySummary): string {
  if (summary.soldOut)
    return 'error'
  if (summary.limited)
    return 'warning'
  if (summary.effectiveRemaining <= 5)
    return 'error'
  if (summary.effectiveRemaining <= 10)
    return 'warning'

  return 'success'
}

/**
 * Get human-readable availability label
 */
export function getAvailabilityLabel(summary: AvailabilitySummary): string {
  if (summary.soldOut)
    return 'Volzet'
  if (summary.limited)
    return 'Beperkte beschikbaarheid'
  if (summary.effectiveRemaining === 1)
    return '1 plaats beschikbaar'

  return `${summary.effectiveRemaining} plaatsen beschikbaar`
}

/**
 * Composable wrapper for reactive usage
 */
export const useTicketAvailability = () => {
  return {
    buildAvailabilitySummary,
    isTicketTypeSoldOut,
    getDayAvailability,
    getWeekTicketAvailability,
    getCapacityPercentage,
    getCapacityColor,
    getAvailabilityLabel,
  }
}
